// Heart Compatibility Page
// Real people, ranked against the MBTI type the viewer actually earned.
//
// This page used to render three hardcoded strangers — Alexander, Jessica and
// Ryan — with stock photos, invented bios, invented match percentages, and ids
// (`user-1`, `user-2`, `user-3`) that matched no row in the database, so "View
// Profile" led nowhere. In a dating product that is not a placeholder; it is
// the product lying about who is on it.
//
// Everything below is real or absent. Candidates come from
// /heart-games/compatible-users (people who have completed a game, minus
// blocks, inactive and incomplete profiles), and the score comes from
// ScoringService.compareTypes — the same rule the sixteen-type list uses.
// When there is nobody to show, the page says so.

import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  HeartGamesContainer,
  HeartGamesHeader,
  HeartGamesTitle,
  HeartGamesButton,
  HeartGamesLoading,
  HeartGamesError,
} from '../../styles/HeartGames/Shared.styles';
import { GameService, CompatibleCandidate } from '../../services/HeartGames/gameService';
import { ScoringService } from '../../services/HeartGames/scoringService';
import { HeartScore } from '../../types/heartGames';

interface RankedMatch extends CompatibleCandidate {
  compatibility: number;
  reason: string;
  typeName: string;
}

export const HeartCompatibility: React.FC = () => {
  const navigate = useNavigate();
  const { gameId } = useParams<{ gameId: string }>();

  const [status, setStatus] = React.useState<'loading' | 'ready' | 'no-result' | 'error'>('loading');
  const [userType, setUserType] = React.useState<string | null>(null);
  const [matches, setMatches] = React.useState<RankedMatch[]>([]);

  React.useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // Both halves are needed: their type is meaningless without yours.
        const [session, candidates] = await Promise.all([
          GameService.getLatestSession(gameId),
          GameService.getCompatibleUsers(50),
        ]);
        if (cancelled) return;

        const score = session?.score as HeartScore | undefined;
        const type = score?.type;
        if (!ScoringService.isValidType(type)) {
          setStatus('no-result');
          return;
        }

        const ranked = candidates
          .map((candidate) => {
            const comparison = ScoringService.compareTypes(type!, candidate.mbtiType);
            if (!comparison) return null;
            return {
              ...candidate,
              compatibility: comparison.score,
              reason: comparison.description,
              typeName: ScoringService.getTypeInfo(candidate.mbtiType).name,
            } as RankedMatch;
          })
          .filter((match): match is RankedMatch => match !== null)
          // Best first; name breaks ties so the order does not shuffle between
          // loads for people who score identically.
          .sort((a, b) => b.compatibility - a.compatibility || a.name.localeCompare(b.name));

        setUserType(type!);
        setMatches(ranked);
        setStatus('ready');
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();

    return () => { cancelled = true; };
  }, [gameId]);

  const handleBack = () => {
    navigate(`/heart-games/${gameId || 'heart-choices-1'}/results`);
  };

  const handleViewProfile = (userId: string) => {
    navigate(`/profile/view/${userId}`);
  };

  const scoreColor = (score: number) => {
    if (score >= 90) return '#26de81';
    if (score >= 80) return '#feca57';
    return '#ff9ff3';
  };

  const header = (
    <HeartGamesHeader>
      <div>
        <HeartGamesTitle>💕 Heart Compatibility</HeartGamesTitle>
        <p style={{ margin: 0, fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)' }}>
          {userType
            ? `People who have played, ranked against your ${userType}`
            : 'People whose personality type fits yours'}
        </p>
      </div>
      <HeartGamesButton variant="outline" onClick={handleBack}>
        ← Back
      </HeartGamesButton>
    </HeartGamesHeader>
  );

  if (status === 'loading') {
    return (
      <HeartGamesContainer>
        {header}
        <HeartGamesLoading>Finding people who fit your type...</HeartGamesLoading>
      </HeartGamesContainer>
    );
  }

  if (status === 'error') {
    return (
      <HeartGamesContainer>
        {header}
        <HeartGamesError>We couldn't load your matches. Please try again.</HeartGamesError>
      </HeartGamesContainer>
    );
  }

  // No score of your own: there is nothing to compare against, and inventing
  // one would be the same lie in a different place.
  if (status === 'no-result') {
    return (
      <HeartGamesContainer>
        {header}
        <div style={{ textAlign: 'center', padding: '3rem 1.5rem', color: 'var(--text-secondary)' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎴</div>
          <h3 style={{ color: 'var(--text-primary)', margin: '0 0 0.5rem 0' }}>
            Play a Heart Game first
          </h3>
          <p style={{ margin: '0 0 1.5rem 0' }}>
            Compatibility needs your personality type, and you don't have one yet.
          </p>
          <HeartGamesButton onClick={() => navigate('/heart-games')}>
            Play a game
          </HeartGamesButton>
        </div>
      </HeartGamesContainer>
    );
  }

  return (
    <HeartGamesContainer>
      {header}

      {matches.length === 0 ? (
        // Honest empty state. Early on, few people have finished a game, and
        // that is a real fact about the app rather than a reason to invent
        // profiles to fill the grid.
        <div style={{ textAlign: 'center', padding: '3rem 1.5rem', color: 'var(--text-secondary)' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
          <h3 style={{ color: 'var(--text-primary)', margin: '0 0 0.5rem 0' }}>
            Nobody to compare with yet
          </h3>
          {/* No button here: "Find More Matches" below goes to the same place,
              and two buttons to /social-feed a few pixels apart is just asking
              the same question twice. */}
          <p style={{ margin: 0 }}>
            Compatibility only works between two people who have both played, and
            nobody else has finished a Heart Game yet. Check back soon.
          </p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '2rem',
          marginBottom: '2rem',
        }}>
          {matches.map((match) => (
            <div
              key={match.userId}
              style={{
                background: 'var(--bg-card)',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                transition: 'transform 0.2s ease',
                cursor: 'pointer',
              }}
              onClick={() => handleViewProfile(match.userId)}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-5px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <div style={{ position: 'relative', height: '200px', background: 'var(--bg-secondary)' }}>
                {match.photo ? (
                  <img
                    src={match.photo}
                    alt={match.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  // A real person without a photo is still a real person. Show
                  // a placeholder rather than dropping them from the results.
                  <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '3rem',
                  }}>
                    🙂
                  </div>
                )}
                <div style={{
                  position: 'absolute',
                  bottom: '10px',
                  right: '10px',
                  background: 'rgba(0, 0, 0, 0.7)',
                  color: scoreColor(match.compatibility),
                  padding: '4px 8px',
                  borderRadius: '20px',
                  fontSize: '0.85rem',
                  fontWeight: 'bold',
                  backdropFilter: 'blur(4px)',
                }}>
                  {match.compatibility}% Match
                </div>
              </div>

              <div style={{ padding: '1.5rem' }}>
                <h3 style={{ margin: '0 0 0.5rem 0', color: 'var(--text-primary)', fontSize: '1.25rem' }}>
                  {match.name}{match.age ? `, ${match.age}` : ''}
                </h3>
                {match.location && (
                  <p style={{ margin: '0 0 1rem 0', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                    {match.location}
                  </p>
                )}

                <div style={{
                  display: 'inline-block',
                  background: 'rgba(255, 140, 66, 0.1)',
                  color: '#ff8c42',
                  padding: '4px 12px',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  marginBottom: '1rem',
                  fontWeight: 500,
                }}>
                  {match.typeName} ({match.mbtiType})
                </div>

                {/* Why, not just how much. The number alone reads as a
                    measurement; the reason keeps it a suggestion. */}
                <p style={{
                  margin: '0 0 1rem 0',
                  color: 'var(--text-secondary)',
                  fontSize: '0.9rem',
                  lineHeight: 1.5,
                }}>
                  {match.reason}
                </p>

                {match.bio && (
                  <p style={{
                    margin: '0 0 1rem 0',
                    color: 'var(--text-secondary)',
                    fontSize: '0.95rem',
                    lineHeight: 1.5,
                    fontStyle: 'italic',
                  }}>
                    "{match.bio}"
                  </p>
                )}

                <HeartGamesButton
                  style={{ width: '100%', marginTop: '0.5rem', justifyContent: 'center' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewProfile(match.userId);
                  }}
                >
                  View Profile
                </HeartGamesButton>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '2rem' }}>
        <HeartGamesButton onClick={() => navigate('/social-feed')}>
          Find More Matches
        </HeartGamesButton>
      </div>
    </HeartGamesContainer>
  );
};
